// src/components/modals/ConversationTitleModal.tsx

import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, ModalHeader, ModalHeading } from "@twilio-paste/modal";
import { Box } from "@twilio-paste/core";

import { AppState, actionCreators } from "../../store";
import { getTranslation } from "../../utils/localUtils";
import CustomerPickerModal from "./CustomerPickerModal";

import { TemplateDto } from "../../dto/template";
import {
  createNewChatFromContact,
  fetchContactConversation,
  fetchTemplates,
} from "../../store/action-creators";
import { TemplateOnlyComposer } from "../new-conversations/NewConversationContainer/TemplateOnlyComposer";
import { bindActionCreators } from "redux";
import {
  successNotification,
  unexpectedErrorNotification,
} from "../../helpers";
import { PARTICIPANT_MESSAGES } from "../../constants";

interface ConversationTitleModalProps {
  title: string;
  isModalOpen: boolean;
  onCancel: () => void;
  onSave: (title: string) => Promise<void>;
  type: string;
}

// tipo "row" che arriva dal CustomerPickerModal
type CustomerRow = {
  customerCode: string;
  fullName: string;
  number: string;
  conversation?: {
    id: string;
    // altri campi se ci sono, ma non sono necessari qui
  } | null;
};

/**
 * Logica riusabile per ConversationTitleModal:
 * - gestisce stato titolo (se ancora ti serve)
 * - gestisce cancel
 * - gestisce flusso click chat:
 *    - se esiste conversation -> GET by id
 *    - altrimenti -> apri modale template e poi POST /new-chat
 */
function useConversationTitleModalLogic(props: ConversationTitleModalProps) {
  const dispatch = useDispatch();
  const local = useSelector((state: AppState) => state.local);
  const templates = useSelector(
    (state: AppState) => state.templates
  ) as TemplateDto[];
  const operator = useSelector((state: AppState) => state.operator);

  const [title, setTitle] = useState(props.title);
  const [error, setError] = useState("");
  const [isFormDirty, setFormDirty] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // per flusso "select template + new-chat"
  const [selectedRow, setSelectedRow] = useState<CustomerRow | null>(null);
  const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);
  const [isTemplateSending, setTemplateSending] = useState(false);

  // sincronizza titolo se cambia da fuori
  useEffect(() => {
    if (props.title !== title) {
      setTitle(props.title);
      setFormDirty(false);
      setError("");
    }
  }, [props.title]);

  const onCancel = useCallback(() => {
    setError("");
    setFormDirty(false);
    setTitle(props.title);
    setSelectedRow(null);
    setTemplateModalOpen(false);
    props.onCancel();
  }, [props]);

  const isBadTitle = title.length < 1 || title.trim() === props.title;

  const onSaveInternal = useCallback(async () => {
    if (title.length < 1) return;

    setError("");

    try {
      await props.onSave(title);
    } catch (e) {
      setError((e as Error).message ?? "");
    } finally {
      setLoading(false);
      setTitle(props.title);
      setFormDirty(false);
    }
  }, [title, props]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.preventDefault();
      if (isBadTitle || isLoading) return;
      setLoading(true);
      void onSaveInternal();
    },
    [isBadTitle, isLoading, onSaveInternal]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (isLoading) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [isLoading, onCancel]
  );

  // carico i template una sola volta se non presenti
  useEffect(() => {
    if (!templates || templates.length === 0) {
      (dispatch as any)(fetchTemplates()).catch(() => {
        // opzionale: error handling
      });
    }
  }, [templates?.length, dispatch]);

  const activeTemplates = (templates ?? []).filter(
    (t) =>
      t.isActive &&
      (t.teams.length === 0 ||
        t.teams.some((team) => team.id === operator?.teamId))
  );
  const { addNotifications } = bindActionCreators(actionCreators, dispatch);

  const handleChatClick = useCallback(
    async (row: CustomerRow) => {
      const convId = row?.conversation?.id || null;

      if (convId) {
        try {
          setLoading(true);
          await (dispatch as any)(fetchContactConversation(convId));
          // chiudo il modal di selezione contatto
          onCancel();
        } catch (e) {
          setError(
            (e as Error)?.message ??
              "Errore durante il caricamento della conversazione."
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      // caso 2: niente conversazione -> apro modale per selezione template
      if (!activeTemplates.length) {
        unexpectedErrorNotification(
          "Nessun template disponibile.",
          addNotifications
        );
        return;
      }
      setSelectedRow(row);
      setTemplateModalOpen(true);
    },
    [dispatch, onCancel]
  );

  const handleSendTemplateNewChat = useCallback(
    async (tpl: TemplateDto, vars: Record<string, string>) => {
      if (!selectedRow) return;

      const payload = {
        customerCode: selectedRow.customerCode,
        phoneNumber: selectedRow.number,
        templateId: tpl.id,
        variables: vars,
      };

      try {
        setTemplateSending(true);
        await (dispatch as any)(createNewChatFromContact(payload));

        // se tutto ok:
        setTemplateModalOpen(false);
        setSelectedRow(null);
        onCancel(); // chiudo anche il modal principale
      } catch (e) {
        setError(
          (e as Error)?.message ??
            "Errore durante la creazione della nuova conversazione."
        );
      } finally {
        setTemplateSending(false);
      }
    },
    [dispatch, selectedRow, onCancel]
  );

  const modalTranslatedTitle = getTranslation(local, "newConvo") || props.title;

  return {
    local,
    title,
    setTitle,
    error,
    isFormDirty,
    setFormDirty,
    isLoading,
    setLoading,
    isBadTitle,
    onCancel,
    onSubmit,
    onKeyDown,
    handleChatClick,
    modalTranslatedTitle,
    // nuovo per template modal
    isTemplateModalOpen,
    setTemplateModalOpen,
    activeTemplates,
    isTemplateSending,
    handleSendTemplateNewChat,
  };
}

const ConversationTitleModal: React.FC<ConversationTitleModalProps> = (
  props
) => {
  const {
    modalTranslatedTitle,
    onCancel,
    handleChatClick,
    isTemplateModalOpen,
    setTemplateModalOpen,
    activeTemplates,
    isTemplateSending,
    handleSendTemplateNewChat,
  } = useConversationTitleModalLogic(props);

  return (
    <>
      {/* Modal di selezione contatto */}
      <CustomerPickerModal
        isOpen={props.isModalOpen}
        title={modalTranslatedTitle}
        onDismiss={onCancel}
        onChatClick={handleChatClick}
      />

      {/* Modal di selezione template per NEW CHAT */}
      <Modal
        ariaLabelledby="new-chat-template-heading"
        isOpen={isTemplateModalOpen}
        onDismiss={() => setTemplateModalOpen(false)}
        size="default"
      >
        <ModalHeader>
          <ModalHeading as="h3" id="new-chat-template-heading">
            Seleziona un template
          </ModalHeading>
        </ModalHeader>

        <Box paddingX="space70" paddingBottom="space70" paddingTop="space40">
          <TemplateOnlyComposer
            templates={activeTemplates}
            onSend={handleSendTemplateNewChat}
            disabled={isTemplateSending}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ConversationTitleModal;
